/**
 * MachVive - SSE to PubSub Bridge
 *
 * Bridges Server-Sent Events (SSE) messages from mvvSSEther to application PubSub topics.
 * Automatically routes SSE messages with topic configurations to the appropriate PubSub channels.
 *
 * @example
 * // Initialize after mvvSSEther is ready
 * MachVive.init();
 *
 * // SSE message format:
 * // {
 * //   "data": {
 * //     "package": {
 * //       "cfg": "CONVERSATION_PROMPT_CANVAS",  // AppBroadcast topic
 * //       "msg": "Hello from server"             // Message payload
 * //     }
 * //   }
 * // }
 *
 * @class
 * @static
 */

// Import AppBroadcast for topic validation
// Note: This will be available in ES module context
import AppBroadcast from './appBroadcast.js';

class MachVive {
    /**
     * Initialization state
     * @private
     */
    static _initialized = false;

    /**
     * Debug mode flag
     * @private
     */
    static _debug = false;

    /**
     * Initialize MachVive bridge
     * Binds to mvvSSEther message callbacks and routes to PubSub
     *
     * NOTE: This is called automatically by mvvSSEther when it detects MachVive.
     * You can also call it manually after mvvSSEther is loaded.
     *
     * @param {Object} options - Configuration options
     * @param {boolean} options.debug - Enable debug logging
     * @returns {MachVive} Returns class for chaining
     * @public
     */
    static init(options = {}) {
        if (this._initialized) {
            console.warn('[MachVive] Already initialized, skipping duplicate initialization');
            return this;
        }

        this._debug = options.debug || false;

        // Check if mvvSSEther is available
        if (!window.mvvSSEther) {
            console.error('[MachVive] mvvSSEther not found - cannot initialize bridge');
            console.error('[MachVive] Make sure mvvSSEther snippet is loaded before MachVive.init()');
            return this;
        }

        console.log('[MachVive] Initializing SSE to PubSub bridge...');

        // Bind to mvvSSEther message callback
        const boundCallback = (messagePackage) => {
            this._handleSSEMessage(messagePackage);
        };

        window.mvvSSEther.onmessage(boundCallback);

        this._initialized = true;

        console.log('[MachVive] ✓ SSE to PubSub bridge initialized successfully');
        console.log('[MachVive] ✓ Bound callback to mvvSSEther.onmessage');

        return this;
    }

    /**
     * Handle incoming SSE message
     * Routes messages with valid topics to PubSub
     *
     * @param {Object} messagePackage - SSE message package from mvvSSEther
     * @param {string} messagePackage.id - Event ID
     * @param {Object} messagePackage.data - Decrypted message data
     * @param {string} messagePackage.raw - Raw encrypted data
     * @param {string} messagePackage.timestamp - Message timestamp
     * @private
     */
    static _handleSSEMessage(messagePackage) {
        try {
            if (this._debug) {
                console.log('[MachVive] SSE message received:', messagePackage);
            }

            // Extract package data
            const data = messagePackage?.data;
            if (!data) {
                if (this._debug) {
                    console.log('[MachVive] No data in message, skipping');
                }
                return;
            }

            // Extract package object (try data.data first, then data.package, then data itself)
            const packageData = data.data || data.package || data;

            if (!packageData) {
                if (this._debug) {
                    console.log('[MachVive] No package found in message, skipping');
                }
                return;
            }

            // Extract cfg (may be JSON string) and msg
            let cfgObject = packageData.cfg;
            const payload = packageData.msg;

            if (this._debug) {
                console.log('[MachVive] cfg (raw):', cfgObject, 'type:', typeof cfgObject, 'length:', cfgObject?.length);
                console.log('[MachVive] msg (raw):', payload);
            }

            // Parse cfg if it's a JSON string
            if (typeof cfgObject === 'string') {
                // Skip empty strings
                if (cfgObject.trim() === '') {
                    if (this._debug) {
                        console.log('[MachVive] cfg is empty string, skipping');
                    }
                    return;
                }

                try {
                    cfgObject = JSON.parse(cfgObject);
                    console.log('[MachVive] cfg (parsed):', cfgObject);
                } catch (parseError) {
                    console.warn('[MachVive] Failed to parse cfg as JSON:', {
                        cfg: cfgObject,
                        cfgLength: cfgObject.length,
                        error: parseError.message
                    });
                    return;
                }
            } else if (!cfgObject) {
                // cfg is null, undefined, or falsy
                if (this._debug) {
                    console.log('[MachVive] cfg is empty/null, skipping');
                }
                return;
            }

            // Extract topic from cfg object
            const topic = cfgObject?.topic;

            if (!topic) {
                // No topic - this is normal for non-routed messages (e.g., CONNECTED events)
                if (this._debug) {
                    console.log('[MachVive] No topic found in cfg, skipping');
                }
                return;
            }

            console.log('[MachVive] Extracted topic:', topic);

            // Check if topic exists in AppBroadcast
            if (!this._isValidTopic(topic)) {
                console.warn(`[MachVive] Topic '${topic}' not found in AppBroadcast, skipping`);
                return;
            }

            // Publish to PubSub
            this._publishToPubSub(topic, payload, messagePackage, data);

        } catch (error) {
            console.error('[MachVive] Error handling SSE message:', error);
        }
    }

    /**
     * Validate if topic exists in AppBroadcast
     *
     * @param {string} topic - Topic name to validate
     * @returns {boolean} True if topic exists in AppBroadcast
     * @private
     */
    static _isValidTopic(topic) {
        // AppBroadcast is imported at top of file
        // Check if topic exists in AppBroadcast
        if (!AppBroadcast || !AppBroadcast.hasOwnProperty(topic)) {
            console.warn(`[MachVive] Topic '${topic}' not found in AppBroadcast`);
            return false;
        }
        return true;
    }

    /**
     * Publish message to PubSub topic
     *
     * @param {string} topic - PubSub topic name (from AppBroadcast)
     * @param {*} payload - Message payload (package.msg)
     * @param {Object} messagePackage - Original SSE message package (for metadata)
     * @param {Object} fullData - Full decrypted message data (includes all fields)
     * @private
     */
    static _publishToPubSub(topic, payload, messagePackage, fullData) {
        try {
            // Check if PubSub is available (global from pubsub-js)
            if (!window.PubSub && typeof PubSub === 'undefined') {
                console.error('[MachVive] PubSub not available');
                return;
            }

            const PubSubLib = window.PubSub || PubSub;

            // Build data package for PubSub
            const pubsubData = {
                message: payload,
                metadata: {
                    source: 'sse',
                    sseEventId: messagePackage.id,
                    timestamp: messagePackage.timestamp,
                    appGuid: fullData.app_guid,
                    channelGuid: fullData.channel_guid,
                    eventAction: fullData.event_action,
                    status: fullData.status,
                    class: fullData.class,
                    raw: fullData
                }
            };

            console.log(`[MachVive] Publishing to topic '${topic}':`, pubsubData);

            // Publish to PubSub using AppBroadcast topic
            const topicString = AppBroadcast[topic];
            console.log(`[MachVive] AppBroadcast.${topic} = '${topicString}'`);

            PubSubLib.publish(topicString, pubsubData);

            console.log(`[MachVive] ✓ Published SSE message to topic: ${topic}`);

        } catch (error) {
            console.error(`[MachVive] Error publishing to topic '${topic}':`, error);
        }
    }

    /**
     * Enable/disable debug logging
     *
     * @param {boolean} enabled - Enable debug mode
     * @returns {MachVive} Returns class for chaining
     * @public
     */
    static debug(enabled) {
        this._debug = enabled;
        console.log(`[MachVive] Debug mode ${enabled ? 'enabled' : 'disabled'}`);
        return this;
    }

    /**
     * Check if MachVive is initialized
     *
     * @returns {boolean} True if initialized
     * @public
     */
    static isInitialized() {
        return this._initialized;
    }

    /**
     * Reset MachVive (for testing/debugging)
     *
     * @returns {MachVive} Returns class for chaining
     * @public
     */
    static reset() {
        this._initialized = false;
        this._debug = false;
        console.log('[MachVive] Reset complete');
        return this;
    }
}

// Export for CommonJS (Node.js)
if (typeof module !== 'undefined' && module.exports) {
    module.exports = MachVive;
}

// Export for ES modules (default export)
export default MachVive;

// Make available globally
if (typeof window !== 'undefined') {
    window.MachVive = MachVive;
}
